// src/components/ExperienceHighlights.styles.ts
//
// Home "Experience across real systems" section.
//
// The four domains are laid out as nodes on a connected spine rather than as
// four cards. The spine runs horizontally on desktop and vertically on mobile,
// with a pulse travelling along each connector to read as systems exchanging
// data. The active node drops a stem into the detail panel below, so the panel
// is visibly attached to the node it describes.
//
// There is deliberately no date, no project name and no link per node. The
// resume documents the position rather than the individual engagements, so a
// dated timeline here would be a fabrication. See aboutConfig.timeline.note.
import styled from 'styled-components';
import { motion } from 'framer-motion';

import { minDevice } from '../styles/breakpoints';

const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

export const HighlightsRoot = styled.section`
  position: relative;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => theme.background};
  padding: 4.5rem 0;

  @media ${minDevice.laptop} {
    padding: 6rem 0;
  }
`;

export const HighlightsContainer = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
`;

export const Header = styled(motion.header)`
  margin-bottom: 2.75rem;
`;

export const Eyebrow = styled(motion.p)`
  margin: 0 0 1rem;
  font-family: ${monoStack};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const Heading = styled(motion.h2)`
  margin: 0;
  font-size: 1.6rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
  font-weight: 700;
  color: ${({ theme }) => theme.headingColor};

  @media ${minDevice.tablet} {
    font-size: 2rem;
  }
`;

export const Lede = styled(motion.p)`
  margin: 1.25rem 0 0;
  max-width: 58ch;
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* ------------------------------------------------------------ the spine */

/* Nodes and connectors are alternating siblings of the spine, so on desktop
   the connectors run horizontally between two nodes, and on mobile they drop
   vertically between them. Nesting the connector inside the node cannot do
   both. */
export const Spine = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  @media ${minDevice.laptop} {
    flex-direction: row;
    align-items: flex-start;
  }
`;

export const Node = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 0;

  @media ${minDevice.laptop} {
    flex: 0 0 auto;
  }
`;

export const NodeButton = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.25rem;
  background: none;
  border: 0;
  cursor: pointer;
  color: ${({ theme, $active }) => ($active ? theme.headingColor : theme.text)};
  transition: color 0.3s ease, transform 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.headingColor};
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 4px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

export const NodeDot = styled.span<{ $active: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.accent : theme.borderColor)};
  border-radius: 50%;
  background: ${({ theme }) => theme.background};
  font-size: 1.05rem;
  color: ${({ theme, $active }) => ($active ? theme.headingColor : theme.accent)};
  transition: border-color 0.3s ease, color 0.3s ease, background 0.3s ease;

  /* Active node gets a filled treatment so the state is not colour-only. */
  ${({ $active, theme }) =>
    $active &&
    `
      background: ${theme.accent};
      color: ${theme.background};
    `}
`;

export const NodeIndex = styled.span`
  font-family: ${monoStack};
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.accent};
  opacity: 0.8;
`;

export const NodeLabel = styled.span<{ $active: boolean }>`
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme, $active }) => ($active ? theme.headingColor : theme.colors.secondary)};
  transition: color 0.3s ease;
`;

export const Connector = styled.span`
  flex: 1 1 0;
  position: relative;
  min-height: 1.5rem;
  margin: 0.4rem 0;

  @media ${minDevice.laptop} {
    min-height: 0;
    min-width: 1.5rem;
    /* Sit the horizontal connector on the same line as the dot centres. */
    margin: 1.4rem 0 0;
  }
`;

export const ConnectorTrack = styled.span`
  position: absolute;
  background: ${({ theme }) => theme.borderColor};

  /* vertical (mobile) */
  left: 50%;
  top: 0;
  bottom: 0;
  width: 1px;
  transform: translateX(-50%);

  @media ${minDevice.laptop} {
    /* horizontal (desktop) */
    left: 0;
    right: 0;
    top: 50%;
    bottom: auto;
    width: auto;
    height: 1px;
    transform: translateY(-50%);
  }
`;

export const ConnectorPulse = styled(motion.span)`
  position: absolute;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ theme }) => theme.accent};

  left: 50%;
  transform: translateX(-50%);

  @media ${minDevice.laptop} {
    left: 0;
    top: 50%;
    /* Centre on both axes so the dot rides the line rather than straddling it. */
    transform: translate(-50%, -50%);
  }
`;

export const PanelWrap = styled.div`
  position: relative;
  margin-top: 2.75rem;
`;

/* --------------------------------------------------------- detail panel */

export const DetailPanel = styled(motion.div)`
  padding: 1.75rem;
  background: ${({ theme }) => theme.cardBg};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

export const ClientRow = styled.p`
  margin: 0 0 0.75rem;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
  font-family: ${monoStack};
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const ClientName = styled.span`
  color: ${({ theme }) => theme.headingColor};
`;

export const DetailTitle = styled.h3`
  margin: 0;
  font-size: 1.3rem;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.headingColor};

  @media ${minDevice.tablet} {
    font-size: 1.5rem;
  }
`;

export const DetailDescription = styled.p`
  margin: 0.9rem 0 0;
  max-width: 62ch;
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.text};
`;

export const FocusLabel = styled.p`
  margin: 1.5rem 0 0.6rem;
  font-family: ${monoStack};
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const FocusList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const FocusItem = styled.li`
  position: relative;
  padding-left: 1rem;
  font-size: 0.95rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.secondary};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.62em;
    width: 5px;
    height: 1px;
    background: ${({ theme }) => theme.accent};
  }
`;

/* --------------------------------------------------------------- footer */

export const StackNote = styled(motion.p)`
  margin: 2rem 0 0;
  max-width: 72ch;
  font-size: 0.9rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.secondary};

  strong {
    color: ${({ theme }) => theme.headingColor};
    font-weight: 600;
  }
`;

/* CtaRow and Cta were removed with this section's /about button. The same
   borderless-arrow treatment still exists in SelectedWorkPreview.styles.ts and
   ProjectCallToAction.styles.ts; it is duplicated rather than shared, which is
   part of the outstanding shared/ promotion. */
